import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Edit2,
  Mail,
  Plus,
  Trash2,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import type { Subscription } from '@/types'
import {
  deleteSubscription,
  updateSubscription,
  upsertSubscription,
} from '@/hooks/useSubscriptions'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import SubscriptionForm from './SubscriptionForm'

interface SubscriptionListProps {
  subscriptions: Subscription[]
  siteId: string
  isLoading: boolean
}

type SortKey = 'email' | 'enabled' | 'updated_at'

function formatDate(iso: string): string {
  if (!iso) return '-'
  return new Date(iso).toLocaleString()
}

export default function SubscriptionList({
  subscriptions,
  siteId,
  isLoading,
}: SubscriptionListProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Subscription | null>(null)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [listError, setListError] = useState<string | null>(null)

  const [sortKey, setSortKey] = useState<SortKey>('email')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const sortedSubscriptions = useMemo(() => {
    return [...subscriptions].sort((a, b) => {
      let cmp = 0
      switch (sortKey) {
        case 'email':
          cmp = a.email.localeCompare(b.email)
          break
        case 'enabled':
          cmp = Number(a.enabled) - Number(b.enabled)
          break
        case 'updated_at':
          cmp =
            new Date(a.updated_at || 0).getTime() -
            new Date(b.updated_at || 0).getTime()
          break
      }
      return sortOrder === 'asc' ? cmp : -cmp
    })
  }, [subscriptions, sortKey, sortOrder])

  const handleSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortKey(key)
        setSortOrder('asc')
      }
    },
    [sortKey]
  )

  const handleOpenAdd = useCallback(() => {
    setEditing(null)
    setFormError(null)
    setFormOpen(true)
  }, [])

  const handleOpenEdit = useCallback((sub: Subscription) => {
    setEditing(sub)
    setFormError(null)
    setFormOpen(true)
  }, [])

  const handleSave = useCallback(
    async (values: {
      email: string
      notify_info: boolean
      notify_warn: boolean
      notify_error: boolean
      enabled: boolean
    }) => {
      setSaving(true)
      setFormError(null)
      try {
        if (editing) {
          await updateSubscription(siteId, editing.email, {
            notify_info: values.notify_info,
            notify_warn: values.notify_warn,
            notify_error: values.notify_error,
            enabled: values.enabled,
          })
        } else {
          await upsertSubscription(siteId, values)
        }
        queryClient.invalidateQueries({
          queryKey: ['subscriptions', siteId],
        })
        setFormOpen(false)
      } catch (err) {
        setFormError(
          err instanceof Error ? err.message : t('subscriptions.saveError')
        )
      } finally {
        setSaving(false)
      }
    },
    [editing, siteId, queryClient, t]
  )

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return
    setDeleting(true)
    setListError(null)
    try {
      await deleteSubscription(siteId, deleteTarget)
      queryClient.invalidateQueries({
        queryKey: ['subscriptions', siteId],
      })
      setDeleteTarget(null)
    } catch (err) {
      setListError(
        err instanceof Error ? err.message : t('subscriptions.deleteError')
      )
    } finally {
      setDeleting(false)
    }
  }, [deleteTarget, siteId, queryClient, t])

  const handleToggleEnabled = useCallback(
    async (sub: Subscription) => {
      setListError(null)
      try {
        await updateSubscription(siteId, sub.email, { enabled: !sub.enabled })
        queryClient.invalidateQueries({
          queryKey: ['subscriptions', siteId],
        })
      } catch (err) {
        setListError(
          err instanceof Error ? err.message : t('subscriptions.updateError')
        )
      }
    },
    [siteId, queryClient, t]
  )

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <LoadingSpinner size="lg" />
        <p className="text-sm text-gray-500">
          {t('subscriptions.loading')}
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {t('subscriptions.title')}
        </h3>
        <button
          onClick={handleOpenAdd}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t('subscriptions.add')}
        </button>
      </div>

      {listError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {listError}
        </div>
      )}

      {subscriptions.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>{t('subscriptions.none')}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th
                  className="text-left py-3 px-4 font-medium text-gray-700 cursor-pointer select-none hover:bg-gray-50"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center gap-1">
                    {t('subscriptions.email')}
                    {sortKey === 'email' ? (
                      sortOrder === 'asc' ? (
                        <ArrowUp className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
                    )}
                  </div>
                </th>
                <th className="text-center py-3 px-2 font-medium text-gray-700">
                  INFO
                </th>
                <th className="text-center py-3 px-2 font-medium text-gray-700">
                  WARN
                </th>
                <th className="text-center py-3 px-2 font-medium text-gray-700">
                  ERROR
                </th>
                <th
                  className="text-center py-3 px-2 font-medium text-gray-700 cursor-pointer select-none hover:bg-gray-50"
                  onClick={() => handleSort('enabled')}
                >
                  <div className="flex items-center justify-center gap-1">
                    {t('subscriptions.enabled')}
                    {sortKey === 'enabled' ? (
                      sortOrder === 'asc' ? (
                        <ArrowUp className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
                    )}
                  </div>
                </th>
                <th
                  className="text-left py-3 px-4 font-medium text-gray-700 cursor-pointer select-none hover:bg-gray-50"
                  onClick={() => handleSort('updated_at')}
                >
                  <div className="flex items-center gap-1">
                    {t('subscriptions.updatedAt')}
                    {sortKey === 'updated_at' ? (
                      sortOrder === 'asc' ? (
                        <ArrowUp className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowDown className="w-3.5 h-3.5" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 text-gray-300" />
                    )}
                  </div>
                </th>
                <th className="text-right py-3 px-4 font-medium text-gray-700">
                  {t('subscriptions.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedSubscriptions.map((sub) => (
                <tr
                  key={sub.email}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900">
                        {sub.email}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-2 text-center">
                    {sub.notify_info ? (
                      <span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-500" />
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-center">
                    {sub.notify_warn ? (
                      <span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-500" />
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-center">
                    {sub.notify_error ? (
                      <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" />
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-center">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sub.enabled}
                        onChange={() => handleToggleEnabled(sub)}
                        className="sr-only peer"
                      />
                      <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:bg-primary-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4 peer-checked:after:border-white" />
                    </label>
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {formatDate(sub.updated_at)}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleOpenEdit(sub)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                        {t('subscriptions.edit')}
                      </button>
                      <button
                        onClick={() => setDeleteTarget(sub.email)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        {t('subscriptions.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SubscriptionForm
        isOpen={formOpen}
        editingSubscription={editing}
        saving={saving}
        error={formError}
        onSave={handleSave}
        onCancel={() => setFormOpen(false)}
      />

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title={t('subscriptions.deleteConfirmTitle')}
        message={
          deleting
            ? t('subscriptions.deleting')
            : t('subscriptions.deleteConfirmMessage', { email: deleteTarget })
        }
        confirmLabel={t('subscriptions.delete')}
        cancelLabel={t('subscriptions.cancel')}
        isAlert
        confirmDisabled={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
